import { useState } from "react";
import { motion } from "framer-motion";
import { fadeUp, viewportOnce } from "./motion";

const MIN = 165;
const MAX = 220;

const TRANCHES = [
  {
    id: "senior",
    name: "Senior Secured Notes",
    pct: 0.40,
    desc: "Investment-grade notes secured against enclosure tranche hardware and active U.S. Trustee escrow structures.",
  },
  {
    id: "equity",
    name: "Institutional Equity",
    pct: 0.30,
    desc: "Joint-venture energy, mining and extraction counterparties holding tranche-level equity positions.",
  },
  {
    id: "mezzanine",
    name: "Mezzanine Tranche",
    pct: 0.20,
    desc: "Subordinated layer bridging senior debt and equity across Phase I enclosure deployments.",
  },
  {
    id: "reserve",
    name: "Escrowed Reserve",
    pct: 0.10,
    desc: "Escrow-reserved allocation for asset clearings, uptime SLA backing and contingency.",
  },
];

export const CapitalStack = () => {
  const [stack, setStack] = useState(MAX);
  const [active, setActive] = useState(TRANCHES[0].id);

  const activeTranche = TRANCHES.find((t) => t.id === active);
  let cursor = 0;

  return (
    <section data-testid="capital-stack-section" className="border-b border-zinc-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
        <motion.p
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="text-[10px] tracking-[0.35em] uppercase text-zinc-500 mb-6"
        >
          04 / Capital Structure
        </motion.p>

        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
          <motion.h2
            variants={fadeUp}
            custom={1}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
            className="font-display text-2xl sm:text-3xl lg:text-4xl uppercase tracking-tight font-bold text-white max-w-2xl"
          >
            Phase I Capital Stack
          </motion.h2>
          <motion.div
            variants={fadeUp}
            custom={2}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
            className="lg:text-right"
          >
            <p
              data-testid="capital-stack-range"
              className="font-display font-black text-5xl lg:text-7xl tracking-tighter text-white tabular-nums"
            >
              ${stack}M
            </p>
            <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mt-2">
              Programmed envelope ${MIN}M — ${MAX}M
            </p>
          </motion.div>
        </div>

        <motion.div
          variants={fadeUp}
          custom={2}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
        >
          <label
            htmlFor="capital-slider"
            className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-4"
          >
            Tranche Scaling Node — drag to scale the stack
          </label>
          <input
            id="capital-slider"
            data-testid="capital-slider"
            type="range"
            min={MIN}
            max={MAX}
            value={stack}
            onChange={(e) => setStack(Number(e.target.value))}
            className="w-full accent-white cursor-pointer"
          />

          <div
            data-testid="capital-stack-bar"
            className="mt-10 flex h-24 sm:h-28 border border-zinc-800"
          >
            {TRANCHES.map((t, i) => {
              const start = cursor;
              cursor += t.pct;
              const isActive = active === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`tranche-${t.id}`}
                  onClick={() => setActive(t.id)}
                  onTouchStart={() => setActive(t.id)}
                  style={{ width: `${t.pct * 100}%` }}
                  className={`relative h-full text-left transition-colors duration-300 ${
                    i > 0 ? "border-l border-zinc-800" : ""
                  } ${isActive ? "bg-white" : "bg-[#121212] hover:bg-[#1c1c1c]"}`}
                >
                  <span
                    className={`absolute top-3 left-3 text-[9px] tracking-[0.2em] uppercase ${
                      isActive ? "text-zinc-600" : "text-zinc-500"
                    }`}
                  >
                    0{i + 1}
                  </span>
                  <span
                    className={`absolute bottom-3 left-3 font-display font-bold text-sm sm:text-lg tabular-nums ${
                      isActive ? "text-black" : "text-white"
                    }`}
                  >
                    ${(stack * t.pct).toFixed(0)}M
                  </span>
                  <span
                    className={`absolute bottom-3 right-3 text-[9px] tabular-nums hidden sm:inline ${
                      isActive ? "text-zinc-600" : "text-zinc-500"
                    }`}
                  >
                    {(t.pct * 100).toFixed(0)}%
                  </span>
                </button>
              );
            })}
          </div>

          <div
            data-testid="tranche-detail-panel"
            className="border border-t-0 border-zinc-800 bg-[#0d0d0d] px-6 sm:px-8 py-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-10"
          >
            <p className="font-display font-bold uppercase tracking-tight text-white text-base sm:text-lg shrink-0">
              {activeTranche.name}
            </p>
            <p className="text-xs leading-relaxed text-zinc-400 max-w-2xl">
              {activeTranche.desc}
            </p>
            <p className="sm:ml-auto text-[10px] tracking-[0.25em] uppercase text-zinc-500 shrink-0 tabular-nums">
              ${(stack * activeTranche.pct).toFixed(1)}M allocated
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
